"use client";

import { motion } from "framer-motion";
/* -----------------Globals--------------- */
import Image from "next/image";
import Link from "next/link";

import { trackingEvents } from "@/analytics";
import ElementAnalyticsWrapper from "@/analytics/ElementAnalyticsWrapper";
/* -----------------Components--------------- */
import { Container } from "@/components/Container";
import {
	AdaptiveText,
	type AdaptiveTextProps,
	Title,
} from "@/components/Content";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { ImageDetails } from "@/types";
import { ArrowRight, Mails } from "lucide-react";
import { stegaClean } from "next-sanity";

export type JobListing = {
	id: string;
	title: string;
	description: string;
	icon: string;
	team: string;
	location: {
		id: string;
		name: string;
		color: string;
	}[];
	href: string;
};

type Region = {
	id: string;
	name: string;
	color: string;
};

export type OpeningsProps = {
	title: string;
	text: AdaptiveTextProps["portableText"];
	image: ImageDetails;
	id: string;
	className?: string;
	primaryCta?: {
		text: string;
		emailAddress: string;
		subject?: string;
	};
	secondaryCta?: {
		text: string;
		href: string;
	};
	ctaTitle?: string;
	ctaDescription?: string;
};

const JobListingItem = ({ job }: { job: JobListing }) => {
	return (
		<ElementAnalyticsWrapper
			eventName={trackingEvents.JOB_LISTING_CLICK}
			eventProperties={{
				jobTitle: job.title,
				jobRegion: job.location.map((item) => item.name).join(", ") ?? "",
				jobListingUrl: job.href,
			}}
		>
			<div className="group relative py-5">
				<h3 className="font-bold text-gray-900 text-lg">
					{job.href ? (
						<Link href={job.href} target="_blank" rel="noopener noreferrer">
							<span className="-inset-x-4 absolute inset-y-0 z-10 rounded-lg group-hover:bg-black/2" />
							{job.icon && <span className="mr-2">{job.icon}</span>}
							<span className="underline-offset-2 group-hover:underline">
								{job.title}
							</span>
							<div className="absolute inset-0 z-10" />
						</Link>
					) : (
						`${job.icon} ${job.title}`
					)}
				</h3>

				{job.location.length > 0 && (
					<div className="my-2 flex gap-1">
						{job.location
							.filter((region: Region) => !!region.name)
							.map((region: Region) => {
								let color = region.color;
								switch (region.color) {
									case "default":
										color = "green";
										break;
									case "yellow":
										color = "blue";
										break;
									case "pink":
										color = "purple";
										break;
									default:
										color = region.color;
										break;
								}

								return (
									<Badge
										key={region.id}
										style={{
											backgroundColor:
												region.name === "Remote"
													? "var(--color-primary)"
													: `color-mix(in srgb, ${color} 10%, transparent)`,
											color:
												region.name === "Remote"
													? "var(--color-primary-foreground)"
													: color,
											border:
												region.name === "Remote"
													? "none"
													: `1px solid ${color}`,
										}}
									>
										{region.name}
									</Badge>
								);
							})}
					</div>
				)}

				{job.description && (
					<p className="mt-2 line-clamp-3 text-gray-600 text-sm">
						{job.description}
					</p>
				)}
			</div>
		</ElementAnalyticsWrapper>
	);
};

const OpeningsClient = ({
	title,
	text,
	image,
	jobListings,
	id,
	className,
	primaryCta,
	secondaryCta,
	ctaTitle = "Ready to join our team?",
	ctaDescription = "We're always looking for talented individuals to help us build the future.",
}: OpeningsProps & { jobListings: JobListing[] }) => {
	const fadeInMotion = {
		hidden: { opacity: 0, y: 30 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.8,
				type: "spring" as const,
				damping: 12,
				stiffness: 80,
			},
		},
	};

	return (
		<motion.div
			initial={{ opacity: 0 }}
			whileInView={{ opacity: 1 }}
			viewport={{ once: true }}
			transition={{ duration: 0.8 }}
		>
			<section id={id}>
				<Container className={className} containerClassName="pb-32">
					<motion.div
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true }}
						variants={{
							hidden: { opacity: 0 },
							visible: {
								opacity: 1,
								transition: {
									staggerChildren: 0.3,
									delayChildren: 0.4,
								},
							},
						}}
					>
						<motion.div variants={fadeInMotion} className="max-w-[85%]">
							<Title className="text-4xl/[0.9] sm:text-5xl/[0.8] md:text-6xl/[0.8]">
								{title}
							</Title>
						</motion.div>

						<motion.div variants={fadeInMotion} className="max-w-3xl">
							<AdaptiveText
								portableText={text}
								className="mt-4 text-base/7 sm:text-lg/8"
							/>
						</motion.div>

						<div className="mt-16 grid gap-16 lg:grid-cols-[1.5fr_2fr]">
							<motion.div
								variants={{
									hidden: { opacity: 0, scale: 0.9 },
									visible: {
										opacity: 1,
										scale: 1,
										transition: {
											duration: 0.8,
											type: "spring" as const,
											damping: 12,
											stiffness: 80,
										},
									},
								}}
								className="lg:sticky lg:top-0"
							>
								<div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl shadow-xl sm:max-w-md">
									<Image
										src={image.src}
										alt={image.alt}
										className="object-cover"
										fill
										sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
										priority
									/>
									<div className="absolute inset-0 rounded-2xl ring-1 ring-black/10" />
								</div>
							</motion.div>

							<motion.div variants={fadeInMotion}>
								<div className="relative max-w-3xl">
									<div className="lg:scrollbar-thin lg:scrollbar-track-gray-100 lg:scrollbar-thumb-gray-300 lg:hover:scrollbar-thumb-gray-400 lg:max-h-[600px] lg:overflow-y-auto lg:pr-4">
										<div className="divide-y divide-gray-100 px-4">
											{jobListings
												?.filter((job) => !!job.href)
												.map((job, index) => (
													<JobListingItem key={index.toString()} job={job} />
												))}
										</div>
									</div>
									{/* Gradient fade indicator for scrollable content */}
									<div className="pointer-events-none absolute right-0 bottom-0 left-0 hidden h-8 bg-gradient-to-t from-white to-transparent lg:block" />
								</div>

								<div className="mt-10 max-w-3xl space-y-6">
									<div className="rounded-lg bg-gradient-to-br from-primary/10 via-secondary/10 to-primary/10 p-6 shadow-sm">
										<h3 className="font-semibold text-2xl text-gray-900">
											{ctaTitle}
										</h3>
										<p className="mt-2 text-gray-600">{ctaDescription}</p>
										<div className="mt-6 flex flex-col gap-4 sm:max-w-lg sm:flex-row">
											{primaryCta && (
												<Button
													asChild
													variant="default"
													size="lg"
													className="w-full bg-primary hover:bg-primary/90"
												>
													<Link
														href={`mailto:${stegaClean(primaryCta.emailAddress ?? "")}${primaryCta.subject ? `?subject=${encodeURIComponent(stegaClean(primaryCta.subject ?? ""))}` : ""}`}
													>
														<Mails className="mr-2 h-4 w-4" />
														{primaryCta.text || "Contact us"}
													</Link>
												</Button>
											)}
											{secondaryCta && (
												<Button
													asChild
													variant="outline"
													size="lg"
													className="w-full border-secondary/30 text-secondary hover:bg-secondary/10"
												>
													<Link href={secondaryCta.href}>
														{secondaryCta.text || "See all openings"}
														<ArrowRight className="ml-2 h-4 w-4" />
													</Link>
												</Button>
											)}
										</div>
									</div>
								</div>
							</motion.div>
						</div>
					</motion.div>
				</Container>
			</section>
		</motion.div>
	);
};

export default OpeningsClient;
